import { Request } from '../request';
import { ENPIType } from 'types/credentialing';
import type {
    TPayerSuggestionReq,
    TGroupSignAgreementReq,
    AddCredentialingProviderReq,
    SaveOrgCredentialingEnrollmentReq,
    ProviderCredentialingPriorCredentialingDto,
    AddProviderPanelsReq,
    TCredentialingContractView,
} from 'types/credentialing';
import type {
    ProviderCredentialingBasicInfoDto,
    ProviderCredentialingLicensesDto,
    ProviderCredentialingPracticeDto,
    ProviderCredentialingBackgroundDto,
    ProviderCredentialingReferenceDto,
    ProviderCredentialingVerificationDto,
    ProviderCredentialingConfirmDto,
    LicenseDto,
    PayerRequirementsDto,
    IrsLetterDto,
    ProviderCredentialingPayoutAccountDto,
} from './types';

export * from './types';

// #19：传 providerId 时按该 provider 的执照判断，每个 payer 多返回 available / unavailableReason
export const getActivePayerList = (states?: string[], providerId?: number | string) =>
    Request.post({
        url: `/schedule-server/provider/credentialing/payer/active${providerId ? `?providerId=${providerId}` : ''}`,
        params: states?.length ? states : undefined,
    });

export const getUpcomingPayerList = (states?: string[]) =>
    Request.post({
        url: '/schedule-server/provider/credentialing/payer/upcoming',
        params: states?.length ? states : undefined,
    });

export const getCredentialingOverview = () =>
    Request.get({
        url: '/schedule-server/provider/credentialing/aggregate/overview',
    });

// 一次拿齐 7 步 detail。入参是 panel id（provider_credentialing_payer.id）不是 providerId，
// 因此只适用于已提交、存在 panel 行的场景。
export const getCredAllDetail = (credentialingPayerId: number, overrideProviderId?: number | string) =>
    Request.get({
        url: '/schedule-server/provider/credentialing/aggregate/all-detail',
        params: { credentialingPayerId },
        overrideProviderId,
    });

// 入口「What best describes you?」选择 Practice NPI(group) / Individual NPI(solo) 时保存计费 NPI 类型。
// 通用 select/record 接口，recordType 固定 CREDENTIALING_BILLING_NPI_TYPE，value 统一用 ENPIType（大写）。
export const saveCredBillingNpiType = (value: ENPIType) =>
    Request.post({
        url: '/schedule-server/common/provider/select/record/save',
        params: { recordType: 'CREDENTIALING_BILLING_NPI_TYPE', value },
    });

// Review 页「Change billing type」后在入口重选时切换计费 NPI 类型。
// 与 saveCredBillingNpiType（首次选择的通用 record 保存）不同，切换副作用由后端处理：
// Group→Solo 移除组内其它 provider 与 practice 行；Solo→Group 的 practice 行留到 practice 页首次保存时创建；
// provider 级数据与 onboarding_status 均保留。
export const switchCredBillingType = (targetType: ENPIType) =>
    Request.post({
        url: '/schedule-server/provider/credentialing/aggregate/switch-billing-type',
        params: { targetType },
    });

export const getCredentialingProviders = () =>
    Request.get({
        url: '/schedule-server/provider/credentialing/onboarding/group/providers',
    });

export const suggestPayers = (reqs: TPayerSuggestionReq[]) =>
    Request.post({
        url: '/schedule-server/provider/credentialing/payer/suggest',
        params: reqs,
    });

// Group credentialing APIs
export const addGroupCredentialingProvider = (data: AddCredentialingProviderReq) =>
    Request.post({
        url: '/schedule-server/provider/credentialing/onboarding/group/add-provider',
        params: data,
    });

export const addGroupCredentialingProvidersByIds = (providerIds: number[]) =>
    Request.post({
        url: '/schedule-server/provider/credentialing/onboarding/group/add-provider-by-id',
        params: providerIds,
    });

export const deleteGroupCredentialingProvider = (providerId: number) =>
    Request.post({
        url: `/schedule-server/provider/credentialing/onboarding/group/delete-provider?providerId=${providerId}`,
    });

export const getGroupCredentialingEnrollment = () =>
    Request.get({
        url: '/schedule-server/provider/credentialing/onboarding/group/enrollment/detail',
    });

export const saveGroupCredentialingEnrollment = (data: SaveOrgCredentialingEnrollmentReq) =>
    Request.post({
        url: '/schedule-server/provider/credentialing/onboarding/group/enrollment',
        params: data,
    });

export const saveGroupCredentialingEnrollmentPayers = (klarityPayerIds: number[]) =>
    Request.post({
        url: '/schedule-server/provider/credentialing/onboarding/group/enrollment/payers',
        params: klarityPayerIds,
    });

export const signGroupAgreement = (params: TGroupSignAgreementReq) =>
    Request.post({
        url: '/schedule-server/provider/credentialing/onboarding/group/sign-agreement',
        params,
    });

export const getCredentialingPanelList = () =>
    Request.get({
        url: '/schedule-server/provider/credentialing/panel/list',
    });

export const getCredentialingProviderStates = (providerIds: number[]) =>
    Request.post({
        url: '/schedule-server/provider/credentialing/payer/states',
        params: providerIds,
    });

// ===== Provider onboarding 分步问卷接口（PPACredentialingStartPage 向导逐步保存/回填）=====
// 每个接口都可选传 overrideProviderId：group owner 代填某个 provider 时透传给 Request，
// 由 Request 自动加上 OVERRIDE-PROVIDER-ID header（目标为自己时会自动跳过）。
const ONBOARDING_BASE = '/schedule-server/provider/credentialing/onboarding';

type OverrideProviderId = number | string | undefined;

// Step 1 · Basic information
export const getCredBasicInfo = (overrideProviderId?: OverrideProviderId) =>
    Request.get({ url: `${ONBOARDING_BASE}/basic-info/detail`, overrideProviderId });

export const saveCredBasicInfo = (params: ProviderCredentialingBasicInfoDto, overrideProviderId?: OverrideProviderId) =>
    Request.post({ url: `${ONBOARDING_BASE}/basic-info`, params, overrideProviderId });

// Step 2 · Licenses
export const getCredLicenses = (overrideProviderId?: OverrideProviderId) =>
    Request.get({ url: `${ONBOARDING_BASE}/licenses/detail`, overrideProviderId });

export const saveCredLicenses = (params: ProviderCredentialingLicensesDto, overrideProviderId?: OverrideProviderId) =>
    Request.post({ url: `${ONBOARDING_BASE}/licenses`, params, overrideProviderId });

// Step 3 · Practice
export const getCredPractice = (overrideProviderId?: OverrideProviderId) =>
    Request.get({ url: `${ONBOARDING_BASE}/practice/detail`, overrideProviderId });

export const saveCredPractice = (params: ProviderCredentialingPracticeDto, overrideProviderId?: OverrideProviderId) =>
    Request.post({ url: `${ONBOARDING_BASE}/practice`, params, overrideProviderId });

// Step 4 · Malpractice insurance certificate（LicenseDto）
export const getCredMalpractice = (overrideProviderId?: OverrideProviderId) =>
    Request.get({ url: `${ONBOARDING_BASE}/malpractice/detail`, overrideProviderId });

export const saveCredMalpractice = (params: LicenseDto, overrideProviderId?: OverrideProviderId) =>
    Request.post({ url: `${ONBOARDING_BASE}/malpractice`, params, overrideProviderId });

// Step 5 · Background（experiences + education）
export const getCredBackground = (overrideProviderId?: OverrideProviderId) =>
    Request.get({ url: `${ONBOARDING_BASE}/background/detail`, overrideProviderId });

export const saveCredBackground = (
    params: ProviderCredentialingBackgroundDto,
    overrideProviderId?: OverrideProviderId,
) => Request.post({ url: `${ONBOARDING_BASE}/background`, params, overrideProviderId });

// Step 6 · References（body 为数组）
export const getCredReferences = (overrideProviderId?: OverrideProviderId) =>
    Request.get({ url: `${ONBOARDING_BASE}/references/detail`, overrideProviderId });

export const saveCredReferences = (
    params: ProviderCredentialingReferenceDto[],
    overrideProviderId?: OverrideProviderId,
) => Request.post({ url: `${ONBOARDING_BASE}/references`, params, overrideProviderId });

// Step 7 · Verification
export const getCredVerification = (overrideProviderId?: OverrideProviderId) =>
    Request.get({ url: `${ONBOARDING_BASE}/verification/detail`, overrideProviderId });

// #20：把 EHR 签名绑定为 credentialing 签名（提交后补签 / Settings 改签名共用）。先用签名弹窗
// /provider/signature/create 建好（group 切 header 到该 provider），再一次提交这次加了签名的 provider。
// 只有 practice owner / admin 能调；solo 本人算 owner
export const bindCredentialingSignature = (providerSignatures: { providerId: number; signatureId: number }[]) =>
    Request.post({ url: `${ONBOARDING_BASE}/signature`, params: { providerSignatures } });

export const saveCredVerification = (
    params: ProviderCredentialingVerificationDto,
    overrideProviderId?: OverrideProviderId,
) => Request.post({ url: `${ONBOARDING_BASE}/verification`, params, overrideProviderId });

// Review · Save Prior credentialing（NPI 预问题弹窗第一步；providerId 在 body 内指定目标 provider）
export const saveProviderPriorCredentialing = (params: ProviderCredentialingPriorCredentialingDto) =>
    Request.post({ url: `${ONBOARDING_BASE}/prior-credentialing`, params });

// Review · 回填 Prior credentialing（弹窗第一步）。
// 该接口没有 providerId 入参，目标 provider 只能靠 overrideProviderId 走 OVERRIDE-PROVIDER-ID header 指定。
export const getProviderPriorCredentialing = (overrideProviderId?: OverrideProviderId) =>
    Request.get({ url: `${ONBOARDING_BASE}/prior-credentialing/detail`, overrideProviderId });

export const addSoloProviderPayers = (klarityPayerIds: number[]) =>
    Request.post({ url: `${ONBOARDING_BASE}/add-payers`, params: klarityPayerIds });

export const addGroupProviderPayersActive = (reqs: AddProviderPanelsReq[]) =>
    Request.post({ url: `${ONBOARDING_BASE}/group/add-payers-active`, params: reqs });

// 代填弹窗第二步：把某个 provider 的 panel 选择存为 DRAFT（不计费、不签约；owner 之后签约提升）
export const saveProviderPanels = (params: AddProviderPanelsReq) =>
    Request.post({ url: `${ONBOARDING_BASE}/group/add-payers`, params });

// 回填弹窗第二步：列出这些 provider 已存在的 payer 行（pending 状态）
export const getGroupProviderPayers = (providerIds: number[]) =>
    Request.post({ url: `${ONBOARDING_BASE}/group/pending-submit-payers`, params: providerIds });

// Review · Confirm（保存 payers + 签署协议 + 生成 deposit billing）
export const confirmCredentialing = (
    params: ProviderCredentialingConfirmDto,
    overrideProviderId?: OverrideProviderId,
    disableErrorNote?: boolean,
) => Request.post({ url: `${ONBOARDING_BASE}/confirm`, params, overrideProviderId, disableErrorNote });

export const getCredentialingPracticePanelList = () =>
    Request.get({ url: '/schedule-server/provider/credentialing/panel/practice/list' });

export const getCredentialingPanelSummary = () =>
    Request.get({ url: '/schedule-server/provider/credentialing/panel/summary' });

export const getCredentialingPracticePanelSummary = () =>
    Request.get({ url: '/schedule-server/provider/credentialing/panel/practice/summary' });

export const markContractSigned = (credentialingPayerId: number) =>
    Request.post({ url: `/schedule-server/provider/credentialing/panel/${credentialingPayerId}/contract-signed` });

const CONTRACT_BASE = '/schedule-server/provider/credentialing';

export const getCredentialingContractBanner = (view: TCredentialingContractView = 'provider') =>
    Request.get({
        url: `${CONTRACT_BASE}/contracts/banner`,
        params: { view },
    });

export const getCredentialingContracts = (view: TCredentialingContractView = 'provider') =>
    Request.get({
        url: `${CONTRACT_BASE}/contracts`,
        params: { view },
    });

export const markCredentialingSignatureSigned = (params: { contractId?: number; panelId?: number }) =>
    Request.post({
        url: `${CONTRACT_BASE}/signatures/signed`,
        params,
    });

export const getActionNeededList = () =>
    Request.get({ url: '/schedule-server/provider/credentialing/action-needed/list' });

export const getGroupActionNeededList = () =>
    Request.get({ url: '/schedule-server/provider/credentialing/action-needed/practice/list' });

// ===== Credentialing V2 =====

// payer-requirements：选 payer 后四步（Service address / Gov program / Optum W-9 / Aetna Focus）的数据，
// 整份 JSON 覆盖保存；group owner 代填时传 overrideProviderId（走 OVERRIDE-PROVIDER-ID header）。
export const getPayerRequirements = (overrideProviderId?: OverrideProviderId) =>
    Request.get({ url: `${ONBOARDING_BASE}/payer-requirements/detail`, overrideProviderId });

export const savePayerRequirements = (params: PayerRequirementsDto, overrideProviderId?: OverrideProviderId) =>
    Request.post({ url: `${ONBOARDING_BASE}/payer-requirements`, params, overrideProviderId });

// CAQH 登录检查：每次调用都是一次真实的 CAQH 登录（失败多了会锁账号），只在用户点按钮时调。
// 同步接口最长约 60s，超时放宽到 75s；同一 provider 正在检查时再调会返回业务错误
export const checkCaqhLogin = (
    params: { username: string; password: string },
    overrideProviderId?: OverrideProviderId,
) =>
    Request.post({
        url: `${ONBOARDING_BASE}/verification/caqh-check`,
        params,
        overrideProviderId,
        config: { timeout: 75 * 1000 },
    });

// IRS Tax ID confirmation letter：provider 级，payer 步骤链与 Settings 共用；字段传 null 不覆盖
export const getIrsLetter = (overrideProviderId?: OverrideProviderId) =>
    Request.get({ url: `${ONBOARDING_BASE}/irs-letter/detail`, overrideProviderId });

export const saveIrsLetter = (params: IrsLetterDto, overrideProviderId?: OverrideProviderId) =>
    Request.post({ url: `${ONBOARDING_BASE}/irs-letter`, params, overrideProviderId });

// 收款账户（保险报销打款），按 providerId upsert；group 场景是 practice 级一份（GET 不管 header 切到谁
// 都返回同一个账户），owner / practice manager 可保存。保存整份覆盖。
export const getCredPayoutAccount = () => Request.get({ url: `${ONBOARDING_BASE}/payout-account/detail` });

export const saveCredPayoutAccount = (params: ProviderCredentialingPayoutAccountDto) =>
    Request.post({ url: `${ONBOARDING_BASE}/payout-account`, params });

// 撤回一个 pending panel（provider 主动发起）；成功后 panel 状态由后端置为 withdrawn/paused
export const withdrawCredentialingPanel = (klarityPayerId: number, overrideProviderId?: OverrideProviderId) =>
    Request.post({
        url: `/schedule-server/provider/credentialing/panel/withdraw/${klarityPayerId}`,
        overrideProviderId,
    });
